import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';

const PREFIX = 'tradournal_brief_';

const SECTIONS = [
  { key: 'market',     icon: '📊', label: 'Market Overview',  placeholder: 'How was the market today? Trends, news, sentiment, key levels…' },
  { key: 'trades',     icon: '📈', label: 'Trades Taken',     placeholder: 'What trades did you take? Ticker, entry, exit, size, outcome…' },
  { key: 'mistakes',   icon: '❌', label: 'Mistakes Made',    placeholder: 'What mistakes did you make? What would you do differently?' },
  { key: 'strategies', icon: '🎯', label: 'Strategies Used',  placeholder: 'What setups or strategies did you use? What worked, what didn\'t?' },
];

const HIGHLIGHTS = [
  { bg: '#fef08a', color: '#713f12', label: 'Yellow' },
  { bg: '#bbf7d0', color: '#14532d', label: 'Green'  },
  { bg: '#fecaca', color: '#7f1d1d', label: 'Red'    },
  { bg: '#bfdbfe', color: '#1e3a5f', label: 'Blue'   },
  { bg: '#fed7aa', color: '#7c2d12', label: 'Orange' },
  { bg: '#e9d5ff', color: '#4c1d95', label: 'Purple' },
];

const DAY_SHORT   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

// ── Rich text editor (contenteditable WYSIWYG) ────────────────────────────
function RichEditor({ value, onChange, placeholder, C }) {
  const ref = useRef(null);
  const isComposing = useRef(false);
  // Track last value to avoid clobbering user cursor on re-render
  const lastHtml = useRef(null); // null so first effect always sets innerHTML

  // Sync external value into DOM only when it truly changed externally
  useEffect(() => {
    if (!ref.current) return;
    if (value !== lastHtml.current) {
      ref.current.innerHTML = value || '';
      lastHtml.current = value || '';
    }
  }, [value]);

  const emit = useCallback(() => {
    if (!ref.current) return;
    const html = ref.current.innerHTML;
    lastHtml.current = html;
    onChange(html);
  }, [onChange]);

  const applyHighlight = useCallback((bg, color) => {
    ref.current?.focus();
    // eslint-disable-next-line no-unused-expressions
    document.execCommand('styleWithCSS', false, true);
    document.execCommand('hiliteColor', false, bg);
    // Also set foreground for readability
    document.execCommand('foreColor', false, color);
    emit();
  }, [emit]);

  const applyBold = useCallback(() => {
    ref.current?.focus();
    document.execCommand('bold');
    emit();
  }, [emit]);

  const clearFmt = useCallback(() => {
    ref.current?.focus();
    document.execCommand('removeFormat');
    emit();
  }, [emit]);

  const isEmpty = !value || value === '<br>' || value === '';

  return (
    <div style={{ borderRadius: 10, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '6px 10px', background: C.card2, borderBottom: `1px solid ${C.border}`, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: C.sub, marginRight: 4, whiteSpace: 'nowrap' }}>Highlight:</span>
        {HIGHLIGHTS.map((hl, i) => (
          <button
            key={i}
            title={hl.label}
            onMouseDown={e => { e.preventDefault(); applyHighlight(hl.bg, hl.color); }}
            style={{
              width: 20, height: 20, borderRadius: 4, border: `1.5px solid ${hl.color}55`,
              background: hl.bg, cursor: 'pointer', flexShrink: 0, padding: 0,
            }}
          />
        ))}
        <div style={{ width: 1, height: 16, background: C.border, margin: '0 4px' }} />
        <button title="Bold" onMouseDown={e => { e.preventDefault(); applyBold(); }}
          style={{ padding: '2px 8px', borderRadius: 5, border: `1px solid ${C.border}`, background: 'transparent', color: C.text, fontWeight: 900, fontSize: 13, cursor: 'pointer' }}>B</button>
        <button title="Clear formatting" onMouseDown={e => { e.preventDefault(); clearFmt(); }}
          style={{ padding: '2px 8px', borderRadius: 5, border: `1px solid ${C.border}`, background: 'transparent', color: C.sub, fontSize: 11, cursor: 'pointer' }}>Clear</button>
      </div>

      {/* Editable area */}
      <div style={{ position: 'relative' }}>
        {isEmpty && (
          <div style={{ position: 'absolute', top: 12, left: 14, right: 14, color: C.sub, fontSize: 13, pointerEvents: 'none', fontStyle: 'italic', opacity: 0.6 }}>
            {placeholder}
          </div>
        )}
        <div
          ref={ref}
          contentEditable
          suppressContentEditableWarning
          onCompositionStart={() => { isComposing.current = true; }}
          onCompositionEnd={() => { isComposing.current = false; emit(); }}
          onInput={() => { if (!isComposing.current) emit(); }}
          onBlur={emit}
          style={{
            minHeight: 120, maxHeight: 260, overflowY: 'auto',
            padding: '12px 14px', outline: 'none',
            color: C.text, fontSize: 13, lineHeight: 1.7,
            background: C.input,
          }}
        />
      </div>
    </div>
  );
}

// ── Section (view + edit) ─────────────────────────────────────────────────
function Section({ icon, label, placeholder, value, editing, onChange, C }) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <span style={{ fontSize: 14 }}>{icon}</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: C.sub, textTransform: 'uppercase', letterSpacing: '0.7px' }}>{label}</span>
      </div>

      {editing ? (
        <RichEditor value={value} onChange={onChange} placeholder={placeholder} C={C} />
      ) : (
        <div style={{
          padding: '12px 14px', borderRadius: 10, minHeight: 100,
          background: C.card2, border: `1px solid ${C.border}`,
          fontSize: 13, lineHeight: 1.7, color: value ? C.text : C.sub,
          cursor: 'text', overflowY: 'auto', maxHeight: 220,
        }}>
          {value
            ? <div dangerouslySetInnerHTML={{ __html: value }} />
            : <span style={{ fontStyle: 'italic', opacity: 0.55 }}>{placeholder}</span>}
        </div>
      )}
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────
function toKey(userId, date) {
  const d = new Date(date); d.setHours(0,0,0,0);
  return `${PREFIX}${userId}_${d.toISOString().split('T')[0]}`;
}
function loadNote(userId, date) {
  try { return JSON.parse(localStorage.getItem(toKey(userId, date))) || {}; } catch { return {}; }
}
function saveNote(userId, date, data) {
  localStorage.setItem(toKey(userId, date), JSON.stringify(data));
}
function hasContent(note) {
  return Object.values(note).some(v => {
    if (!v) return false;
    const stripped = v.replace(/<[^>]*>/g, '').replace(/&[^;]+;/g, '').trim();
    return stripped.length > 0;
  });
}
function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function startOfWeek(date) {
  const d = new Date(date); d.setHours(0,0,0,0);
  d.setDate(d.getDate() - d.getDay());
  return d;
}
function addDays(date, n) {
  const d = new Date(date); d.setDate(d.getDate() + n); return d;
}
function fmtFull(date) {
  return date.toLocaleDateString('en-US', { weekday:'long', month:'long', day:'numeric', year:'numeric' });
}

// ── Main Component ────────────────────────────────────────────────────────
export default function DailyBriefing({ C, cardStyle, inp, userId }) {
  const today = useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d; }, []);

  const [selected,   setSelected]   = useState(today);
  const [weekStart,  setWeekStart]  = useState(() => startOfWeek(today));
  const [note,       setNote]       = useState(() => loadNote(userId, today));
  const [editing,    setEditing]    = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const weekEnd   = addDays(weekStart, 6);
  const weekLabel = weekStart.getMonth() === weekEnd.getMonth()
    ? `${MONTH_NAMES[weekStart.getMonth()]} ${weekStart.getDate()} – ${weekEnd.getDate()}, ${weekStart.getFullYear()}`
    : `${MONTH_NAMES[weekStart.getMonth()]} ${weekStart.getDate()} – ${MONTH_NAMES[weekEnd.getMonth()]} ${weekEnd.getDate()}, ${weekEnd.getFullYear()}`;

  const prevWeek = () => setWeekStart(w => addDays(w, -7));
  const nextWeek = () => setWeekStart(w => addDays(w, 7));

  const selectDay = (day) => {
    if (editing) saveNote(userId, selected, note);
    setSelected(day);
    setNote(loadNote(userId, day));
    setEditing(false);
  };

  const handleSave = () => {
    saveNote(userId, selected, note);
    setEditing(false);
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 2000);
  };

  const handleCancel = () => {
    setNote(loadNote(userId, selected));
    setEditing(false);
  };

  const noteExists = hasContent(note);
  const isDark = C.card !== '#ffffff';
  const lbl = { fontSize: 10, fontWeight: 700, color: C.sub, textTransform: 'uppercase', letterSpacing: '0.8px' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* ── Week navigator ── */}
      <div style={{ ...cardStyle, padding: '20px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <div style={{ color: C.text, fontWeight: 800, fontSize: 16, letterSpacing: '-0.3px' }}>Daily Trading Journal</div>
            <div style={{ color: C.sub, fontSize: 12, marginTop: 3 }}>Select a day to view or add your notes</div>
          </div>
          <button onClick={() => { setWeekStart(startOfWeek(today)); selectDay(today); }}
            style={{ padding: '7px 16px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.card2, color: C.text, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
            Today
          </button>
        </div>

        {/* Week strip */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={prevWeek} style={{ padding: '8px 12px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.card2, color: C.text, cursor: 'pointer', fontWeight: 700, flexShrink: 0 }}>‹</button>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ textAlign: 'center', ...lbl }}>{weekLabel}</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {days.map(day => {
                const isSel = sameDay(day, selected);
                const isTod = sameDay(day, today);
                const isFut = day > today;
                const dotted = hasContent(loadNote(userId, day));
                const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                return (
                  <div key={day.toISOString()} onClick={() => !isFut && selectDay(day)}
                    style={{
                      flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5,
                      padding: '10px 4px', borderRadius: 12, cursor: isFut ? 'default' : 'pointer',
                      background: isSel ? (isDark ? `${C.blue}30` : `${C.blue}18`) : isTod ? (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)') : 'transparent',
                      border: `2px solid ${isSel ? C.blue : isTod ? C.border : 'transparent'}`,
                      opacity: isFut ? 0.35 : 1, transition: 'all 0.15s',
                    }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: isSel ? C.blueBright : C.sub, letterSpacing: '0.5px' }}>{DAY_SHORT[day.getDay()]}</span>
                    <span style={{ fontSize: 18, fontWeight: 900, lineHeight: 1, color: isSel ? C.blueBright : isTod ? C.text : isWeekend ? C.sub : C.text }}>{day.getDate()}</span>
                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: dotted ? C.greenBright : 'transparent' }} />
                  </div>
                );
              })}
            </div>
          </div>
          <button onClick={nextWeek} style={{ padding: '8px 12px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.card2, color: C.text, cursor: 'pointer', fontWeight: 700, flexShrink: 0 }}>›</button>
        </div>
      </div>

      {/* ── Note panel ── */}
      <div style={{ ...cardStyle, padding: '22px 26px' }}>
        {/* Day header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 50, height: 50, borderRadius: 14, flexShrink: 0,
              background: sameDay(selected, today) ? (isDark ? `${C.blue}30` : `${C.blue}18`) : isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
              border: `2px solid ${sameDay(selected, today) ? C.blue : C.border}`,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            }}>
              <span style={{ fontSize: 9, fontWeight: 800, color: sameDay(selected, today) ? C.blueBright : C.sub, letterSpacing: '0.5px', textTransform: 'uppercase' }}>{DAY_SHORT[selected.getDay()]}</span>
              <span style={{ fontSize: 20, fontWeight: 900, lineHeight: 1, color: sameDay(selected, today) ? C.blueBright : C.text }}>{selected.getDate()}</span>
            </div>
            <div>
              <div style={{ color: C.text, fontWeight: 800, fontSize: 15 }}>{fmtFull(selected)}</div>
              <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                {sameDay(selected, today) && <span style={{ fontSize: 11, fontWeight: 700, color: C.blueBright, background: isDark ? `${C.blue}25` : `${C.blue}15`, padding: '2px 8px', borderRadius: 20 }}>Today</span>}
                {noteExists && !editing && <span style={{ fontSize: 11, fontWeight: 700, color: C.greenBright, background: isDark ? `${C.green}20` : `${C.green}15`, padding: '2px 8px', borderRadius: 20 }}>● Saved</span>}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {editing ? (
              <>
                <button onClick={handleCancel} style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.card2, color: C.sub, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>Cancel</button>
                <button onClick={handleSave} style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: savedFlash ? C.green : C.blue, color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 700, transition: 'background 0.3s' }}>{savedFlash ? '✓ Saved!' : 'Save'}</button>
              </>
            ) : (
              <button onClick={() => setEditing(true)} style={{ padding: '8px 18px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.card2, color: C.text, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                {noteExists ? 'Edit Notes' : '+ Add Notes'}
              </button>
            )}
          </div>
        </div>

        {/* Tip banner when editing */}
        {editing && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, padding: '8px 12px', background: `${C.blue}12`, border: `1px solid ${C.blue}33`, borderRadius: 8 }}>
            <span style={{ fontSize: 11, color: C.sub }}>💡 Select any text, then click a colour swatch in the toolbar to highlight it.</span>
          </div>
        )}

        {/* 4 sections grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          {SECTIONS.map(({ key, icon, label, placeholder }) => (
            <Section
              key={key}
              icon={icon}
              label={label}
              placeholder={placeholder}
              value={note[key]}
              editing={editing}
              onChange={val => setNote(prev => ({ ...prev, [key]: val }))}
              C={C}
            />
          ))}
        </div>

        {/* Day nav */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
          <button onClick={() => { const prev = addDays(selected, -1); setWeekStart(startOfWeek(prev)); selectDay(prev); }}
            style={{ padding: '8px 18px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.card2, color: C.text, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
            ← Previous Day
          </button>
          <button
            onClick={() => { const next = addDays(selected, 1); if (next <= today) { setWeekStart(startOfWeek(next)); selectDay(next); } }}
            disabled={sameDay(selected, today)}
            style={{ padding: '8px 18px', borderRadius: 8, border: `1px solid ${C.border}`, background: sameDay(selected, today) ? 'transparent' : C.card2, color: sameDay(selected, today) ? C.sub : C.text, cursor: sameDay(selected, today) ? 'default' : 'pointer', fontSize: 12, fontWeight: 700, opacity: sameDay(selected, today) ? 0.4 : 1 }}>
            Next Day →
          </button>
        </div>
      </div>
    </div>
  );
}
